import { useEffect, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-toastify";

import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/api";

const ResellerLogsModal = ({ onClose, open }) => {
  const { user } = useAuth();

  const [resellers, setResellers] = useState([]);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logSearchTerm, setLogSearchTerm] = useState("");
  const [logSelectedReseller, setLogSelectedReseller] = useState("all");
  const [logResellerSearchTerm, setLogResellerSearchTerm] = useState("");
  const [logSortField, setLogSortField] = useState("createdAt");
  const [logSortDirection, setLogSortDirection] = useState("desc");
  const [logCurrentPage, setLogCurrentPage] = useState(1);
  const [logItemsPerPage, setLogItemsPerPage] = useState(10);
  const [logPagination, setLogPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0,
  });
  const [showResellerDropdown, setShowResellerDropdown] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      try {
        if (user?.role === "admin") {
          const response = await apiService.getResellers({ limit: 100 });

          if (cancelled) return;
          const list = response?.data?.resellers || [];

          setResellers(Array.isArray(list) ? list : []);
        } else {
          const response = await apiService.getMyReseller();

          if (cancelled) return;
          const r = response?.data?.reseller;

          setResellers(r ? [r] : []);
        }
      } catch (err) {
        console.error("Error loading reseller list for filter", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, user?.role]);

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const sortFieldMap = {
        reseller: "created_at",
        actionType: "action_type",
        actionSource: "action_source",
        description: "created_at",
        createdAt: "created_at",
      };

      const apiSortField = sortFieldMap[logSortField] || "created_at";
      const apiSortOrder = logSortDirection.toUpperCase();

      const params = {
        page: logCurrentPage,
        limit: logItemsPerPage,
        sort: apiSortField,
        order: apiSortOrder,
      };

      if (logSearchTerm && logSearchTerm.trim()) {
        params.search = logSearchTerm.trim();
      }

      if (logSelectedReseller && logSelectedReseller !== "all") {
        const selectedResellerObj = resellers.find(
          (r) => r.username === logSelectedReseller
        );

        if (selectedResellerObj) {
          params.resellerId = selectedResellerObj.id;
        }
      }

      const response = await apiService.getResellerLogs(params);
      const responseData = response.data || {};
      const logsData = responseData.logs || [];
      const paginationData = response.pagination || {
        total: 0,
        page: 1,
        limit: logItemsPerPage,
        pages: 0,
      };

      setLogs(Array.isArray(logsData) ? logsData : []);
      setLogPagination(paginationData);
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("Failed to load reseller logs");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    logCurrentPage,
    logItemsPerPage,
    logSortField,
    logSortDirection,
    logSelectedReseller,
  ]);

  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        if (logCurrentPage !== 1) {
          setLogCurrentPage(1);
        } else {
          fetchLogs();
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logSearchTerm]);

  const uniqueLogResellers = [...resellers].map((r) => r.username).sort();

  const filteredDropdownResellers = uniqueLogResellers.filter((reseller) =>
    reseller.toLowerCase().includes(logResellerSearchTerm.toLowerCase())
  );

  const handleLogSort = (field) => {
    if (logSortField === field) {
      setLogSortDirection(logSortDirection === "asc" ? "desc" : "asc");
    } else {
      setLogSortField(field);
      setLogSortDirection("asc");
    }
  };

  const currentLogItems = logs;
  const logTotalPages = logPagination.pages;
  const logIndexOfFirstItem = (logPagination.page - 1) * logPagination.limit;
  const logIndexOfLastItem = Math.min(
    logIndexOfFirstItem + logs.length,
    logPagination.total
  );

  const getLogPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (logTotalPages <= maxVisiblePages) {
      for (let i = 1; i <= logTotalPages; i++) {
        pages.push(i);
      }
    } else {
      if (logCurrentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(logTotalPages);
      } else if (logCurrentPage >= logTotalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = logTotalPages - 3; i <= logTotalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = logCurrentPage - 1; i <= logCurrentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(logTotalPages);
      }
    }

    return pages;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          onClick={() => onClose()}
        >
          <motion.div
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
            exit={{ scale: 0.9, opacity: 0 }}
            initial={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Resellers Log
              </h2>
              <button
                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                onClick={() => onClose()}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    className="input pl-10 pr-10 w-full"
                    placeholder="Search logs..."
                    type="text"
                    value={logSearchTerm}
                    onChange={(e) => {
                      setLogSearchTerm(e.target.value);
                      setLogCurrentPage(1);
                    }}
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  {logSearchTerm && (
                    <button
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      onClick={() => {
                        setLogSearchTerm("");
                        setLogCurrentPage(1);
                      }}
                    >
                      <i className="fas fa-times text-sm" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                {/* Searchable Reseller Filter */}
                <div className="relative w-full sm:w-auto">
                  <div
                    className="input w-full sm:w-64 cursor-pointer flex items-center justify-between"
                    onClick={() =>
                      setShowResellerDropdown(!showResellerDropdown)
                    }
                  >
                    <span
                      className={
                        logSelectedReseller === "all" ? "text-gray-500" : ""
                      }
                    >
                      {logSelectedReseller === "all"
                        ? "All Resellers"
                        : logSelectedReseller}
                    </span>
                    <i
                      className={`fas fa-chevron-${showResellerDropdown ? "up" : "down"} text-gray-400 ml-2`}
                    />
                  </div>
                  {showResellerDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                        <div className="relative">
                          <input
                            className="input w-full text-sm pr-8"
                            placeholder="Search resellers..."
                            type="text"
                            value={logResellerSearchTerm}
                            onChange={(e) =>
                              setLogResellerSearchTerm(e.target.value)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                          {logResellerSearchTerm && (
                            <button
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLogResellerSearchTerm("");
                              }}
                            >
                              <i className="fas fa-times text-sm" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <div
                          className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                          onClick={() => {
                            setLogSelectedReseller("all");
                            setLogCurrentPage(1);
                            setShowResellerDropdown(false);
                            setLogResellerSearchTerm("");
                          }}
                        >
                          All Resellers
                        </div>
                        {filteredDropdownResellers.length === 0 ? (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No resellers found
                          </div>
                        ) : (
                          filteredDropdownResellers.map((reseller) => (
                            <div
                              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                              key={reseller}
                              onClick={() => {
                                setLogSelectedReseller(reseller);
                                setLogCurrentPage(1);
                                setShowResellerDropdown(false);
                                setLogResellerSearchTerm("");
                              }}
                            >
                              {reseller}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Items Per Page */}
                <select
                  className="input w-full sm:w-auto"
                  value={logItemsPerPage}
                  onChange={(e) => {
                    setLogItemsPerPage(Number(e.target.value));
                    setLogCurrentPage(1);
                  }}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
            </div>

            {/* Logs Table */}
            <div className="flex-1 overflow-auto">
              <table className="w-full min-w-max">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleLogSort("reseller")}
                    >
                      <div className="flex items-center gap-1">
                        Reseller
                        {logSortField === "reseller" && (
                          <i
                            className={`fas fa-chevron-${logSortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleLogSort("actionType")}
                    >
                      <div className="flex items-center gap-1">
                        Action Type
                        {logSortField === "actionType" && (
                          <i
                            className={`fas fa-chevron-${logSortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleLogSort("description")}
                    >
                      <div className="flex items-center gap-1">
                        Description
                        {logSortField === "description" && (
                          <i
                            className={`fas fa-chevron-${logSortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                          />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => handleLogSort("createdAt")}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {logSortField === "createdAt" && (
                          <i
                            className={`fas fa-chevron-${logSortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                          />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {logsLoading ? (
                    <tr>
                      <td className="text-center py-12" colSpan="4">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                          <span className="text-gray-500">Loading logs...</span>
                        </div>
                      </td>
                    </tr>
                  ) : currentLogItems.length === 0 ? (
                    <tr>
                      <td
                        className="text-center py-12 text-gray-500"
                        colSpan="4"
                      >
                        No logs found
                      </td>
                    </tr>
                  ) : (
                    currentLogItems.map((log, index) => (
                      <tr
                        className={`${
                          index % 2 === 0
                            ? "bg-gray-50 dark:bg-gray-800/50"
                            : "bg-white dark:bg-gray-900/50"
                        } hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors`}
                        key={log.id}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {log.reseller}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                            {log.actionType}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {log.description}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {new Date(log.createdAt).toLocaleString()}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {logPagination.total > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {logIndexOfFirstItem + 1} to {logIndexOfLastItem} of{" "}
                  {logPagination.total} logs
                </p>
                <div className="flex gap-2">
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={logCurrentPage === 1}
                    onClick={() => setLogCurrentPage(logCurrentPage - 1)}
                  >
                    Previous
                  </button>
                  {getLogPageNumbers().map((page, index) =>
                    page === "..." ? (
                      <span
                        className="px-2 py-1 text-sm text-gray-500"
                        key={`ellipsis-${index}`}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        className={`px-3 py-1 text-sm border rounded-lg transition-colors ${
                          logCurrentPage === page
                            ? "bg-primary-600 text-white border-primary-600"
                            : "border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                        key={page}
                        onClick={() => setLogCurrentPage(page)}
                      >
                        {page}
                      </button>
                    )
                  )}
                  <button
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={logCurrentPage === logTotalPages}
                    onClick={() => setLogCurrentPage(logCurrentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ResellerLogsModal;
